// Coerce form values (always strings or files) into what a schema expects.
// Adapted from @conform-to/zod 1.19.4 `coerceFormValue` (MIT), with its
// wrapper schemas built from `zod/mini` instead of classic zod, so a form
// page does not load classic zod. It rebuilds the schema rather than
// rewriting the values, because a pipe that starts with a transform
// (`z.preprocess`) needs the coercion inside the pipe, on the transform's
// output.
import { lazy, pipe, transform } from 'zod/mini';
import { type $ZodType } from 'zod/v4/core';

type Def = Record<string, unknown> & { type: string };
type Coerce = (value: unknown) => unknown;

const defOf = (schema: $ZodType) => schema._zod.def as unknown as Def;

const isEmptyFile = (value: unknown) =>
  typeof File !== 'undefined' &&
  value instanceof File &&
  value.name === '' &&
  value.size === 0;

/** An empty string or an empty file input means no value. */
const stripEmpty: Coerce = (value) =>
  value === '' || isEmptyFile(value) ? undefined : value;

// Empty text is no value. Text that does not convert is kept, so the schema
// reports it.
const fromText =
  (convert: (text: string) => unknown): Coerce =>
  (value) => {
    if (typeof value !== 'string') return value;
    if (value === '') return undefined;
    try {
      return convert(value);
    } catch {
      return value;
    }
  };

const fail = (): never => {
  throw new Error('Not convertible');
};

const toText: Coerce = (value) => (value === '' ? undefined : value);
const toFile: Coerce = (value) => (isEmptyFile(value) ? undefined : value);
const toNumber = fromText((text) => (text.trim() === '' ? NaN : Number(text)));
const toBoolean = fromText((text) => (text === 'on' ? true : fail()));
const toDate = fromText((text) => {
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? fail() : date;
});
const toBigInt = fromText((text) =>
  text.trim() === '' ? fail() : BigInt(text),
);

// A literal's or enum's values take the coercion of their type.
const BY_TYPE: Record<string, Coerce> = {
  number: toNumber,
  boolean: toBoolean,
  bigint: toBigInt,
};

// Run `coerce` before `target`, keeping whether the target accepts a missing
// value, which zod reads from `optin` and `optout`.
const before = (coerce: Coerce, target: $ZodType): $ZodType => {
  const schema = pipe(transform(coerce), target) as unknown as $ZodType;
  const zod = schema._zod as { optin?: unknown; optout?: unknown };
  if (target._zod.optin) zod.optin = target._zod.optin;
  if (target._zod.optout) zod.optout = target._zod.optout;
  return schema;
};

// Whether the coerced type turns a missing value into `{}` or `[]`.
const materializesMissing = (type: $ZodType): boolean => {
  const def = defOf(type);
  if (def.type in WRAPPERS) {
    return materializesMissing(def['innerType'] as $ZodType);
  }
  if (def.type === 'lazy') {
    return materializesMissing((def['getter'] as () => $ZodType)());
  }
  if (def.type === 'pipe') {
    const input = def['in'] as $ZodType;
    return materializesMissing(
      defOf(input).type === 'transform' ? (def['out'] as $ZodType) : input,
    );
  }
  return def.type === 'array' || def.type === 'object';
};

// The same schema, but a missing object key reaches it, so the coercion can
// turn it into `{}` or `[]`.
const withOptionalInput = (type: $ZodType): $ZodType => {
  const schema = Object.create(Object.getPrototypeOf(type)) as $ZodType;
  const { _zod: _, ...descriptors } = Object.getOwnPropertyDescriptors(type);
  Object.defineProperties(schema, descriptors);
  Object.defineProperty(schema, '_zod', {
    value: Object.create(type._zod, { optin: { value: 'optional' } }),
  });
  return schema;
};

interface Context {
  resolved: Map<$ZodType, $ZodType>;
  resolving: Set<$ZodType>;
}

const coerceShape = (shape: Record<string, $ZodType>, context: Context) =>
  Object.fromEntries(
    Object.entries(shape).map(([key, type]) => {
      const schema = coerceType(type, context);
      return [
        key,
        type._zod.optin !== 'optional' && materializesMissing(type)
          ? withOptionalInput(schema)
          : schema,
      ];
    }),
  );

interface Node {
  type: $ZodType;
  def: Def;
  context: Context;
  rebuild: (changes: Record<string, unknown>) => $ZodType;
  coerce: (type: unknown) => $ZodType;
}
type Handler = (node: Node) => $ZodType;

const leaf =
  (coerce: Coerce): Handler =>
  ({ type }) =>
    before(coerce, type);

const oneOf: Handler = ({ type }) =>
  before(BY_TYPE[typeof [...(type._zod.values ?? [])][0]] ?? toText, type);

// Wrappers around one schema. Those that accept a missing value also read
// an empty input as missing.
const wrap: Handler = ({ def, rebuild, coerce }) => {
  // An empty string that is itself the default is left alone.
  const kept = def['defaultValue'] === '';
  const wrapped = rebuild({
    innerType: kept ? def['innerType'] : coerce(def['innerType']),
  });
  return STRIPS_EMPTY.has(def.type) ? before(stripEmpty, wrapped) : wrapped;
};

const WRAPPERS: Record<string, Handler> = {
  optional: wrap,
  nonoptional: wrap,
  default: wrap,
  prefault: wrap,
  catch: wrap,
  nullable: wrap,
};
const STRIPS_EMPTY = new Set([
  'optional',
  'nonoptional',
  'default',
  'prefault',
]);

const coerceList = (node: Node, key: string) =>
  (node.def[key] as $ZodType[]).map((item) => node.coerce(item));

const missingAsObject: Coerce = (value) => (value === undefined ? {} : value);

// The union picks its option by `propValues`, which a rebuilt option loses.
const discriminatedUnion: Handler = (node) =>
  before(
    missingAsObject,
    node.rebuild({
      options: (node.def['options'] as $ZodType[]).map((option) => {
        const optionDef = defOf(option);
        const coerced =
          optionDef.type === 'object'
            ? build(option, {
                ...optionDef,
                shape: coerceShape(
                  optionDef['shape'] as Record<string, $ZodType>,
                  node.context,
                ),
              })
            : node.coerce(option);
        const zod = coerced._zod as { propValues?: unknown; disc?: unknown };
        zod.propValues = option._zod.propValues;
        zod.disc = (option._zod as { disc?: unknown }).disc;
        return coerced;
      }),
    }),
  );

const build = (type: $ZodType, def: Def) =>
  new (type._zod.constr as unknown as new (def: Def) => $ZodType)(def);

const HANDLERS: Record<string, Handler> = {
  ...WRAPPERS,
  string: leaf(toText),
  file: leaf(toFile),
  number: leaf(toNumber),
  boolean: leaf(toBoolean),
  date: leaf(toDate),
  bigint: leaf(toBigInt),
  enum: oneOf,
  literal: oneOf,
  array: (node) =>
    before(
      (value) => {
        if (Array.isArray(value)) return value;
        if (value === undefined || stripEmpty(value) === undefined) return [];
        return [value];
      },
      node.rebuild({ element: node.coerce(node.def['element']) }),
    ),
  object: (node) =>
    before(
      missingAsObject,
      node.rebuild({
        shape: coerceShape(
          node.def['shape'] as Record<string, $ZodType>,
          node.context,
        ),
      }),
    ),
  union: (node) =>
    [...node.type._zod.traits].some((trait) =>
      trait.includes('DiscriminatedUnion'),
    )
      ? discriminatedUnion(node)
      : node.rebuild({ options: coerceList(node, 'options') }),
  intersection: (node) =>
    node.rebuild({
      left: node.coerce(node.def['left']),
      right: node.coerce(node.def['right']),
    }),
  tuple: (node) => node.rebuild({ items: coerceList(node, 'items') }),
  pipe: (node) =>
    node.rebuild({
      in: node.coerce(node.def['in']),
      out: node.coerce(node.def['out']),
    }),
  lazy: (node) =>
    lazy(() =>
      node.coerce((node.def['getter'] as () => $ZodType)()),
    ) as unknown as $ZodType,
};

const coerceType = (type: $ZodType, context: Context): $ZodType => {
  const known = context.resolved.get(type);
  if (known) return known;
  if (context.resolving.has(type)) {
    return lazy(() => coerceType(type, context)) as unknown as $ZodType;
  }
  context.resolving.add(type);

  const def = defOf(type);
  const schema =
    HANDLERS[def.type]?.({
      type,
      def,
      context,
      rebuild: (changes) => build(type, { ...def, ...changes }),
      coerce: (inner) => coerceType(inner as $ZodType, context),
    }) ?? type;

  context.resolving.delete(type);
  if (schema !== type) context.resolved.set(type, schema);
  return schema;
};

const cache = new WeakMap<$ZodType, $ZodType>();

/**
 * The schema with form-value coercion added: empty strings and empty file
 * inputs become `undefined`, and text becomes a number, boolean (`"on"`),
 * date or bigint where the schema expects one. Text that does not convert
 * is left for the schema to reject.
 */
export const coerceFormValue = <S extends $ZodType>(schema: S): S => {
  let coerced = cache.get(schema);
  if (!coerced) {
    coerced = coerceType(schema, { resolved: new Map(), resolving: new Set() });
    cache.set(schema, coerced);
  }
  return coerced as S;
};
